/**
 * CollectionMark — a deterministic generative "specimen" glyph for a collection.
 *
 * INTERIM PLACEHOLDER. The designers will replace these with real per-collection artwork (in the
 * style of the app's desert-animal avatars) in the coming weeks. Until then this demonstrates the
 * idea: a stable, varied dryland-plant mark per collection at zero asset cost.
 *
 * Swap seam: callers pass the collection's `coverImage` when one exists; only when it's absent do
 * we fall back to this generated silhouette. So dropping in real imagery later is a data change,
 * not a code change.
 *
 * Dialed back to a few clean species (agave/aloe rosette, saguaro, barrel cactus, prickly pear),
 * seeded from the collection id so a collection always draws the same one, in a seeded dryland
 * colour. Kept soft so it reads as a quiet cover motif behind the title.
 */

const PALETTE = [
  '#0D7A91', // teal (primary)
  '#1BAF7A', // cactus green
  '#C9772B', // terracotta
  '#B0892C', // ochre
  '#5B7A3A', // sage
  '#A0552F', // rust
  '#2A78D6', // desert sky
  '#7A5AA7', // dusk violet
];

function mulberry32(a: number) {
  return function () {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

// ── Archetype builders: each returns SVG markup on a 0..100 canvas, base at ~y=92 ──

function rosette(rng: () => number, color: string): string {
  // agave / aloe / yucca — radial blades from a low centre
  const cx = 50;
  const cy = 62 + rng() * 8;
  const blades = 9 + Math.floor(rng() * 6);
  const spread = Math.PI * (0.82 + rng() * 0.15); // fan upward
  const start = -Math.PI / 2 - spread / 2;
  let p = '';
  for (let i = 0; i < blades; i++) {
    const a = start + (i / (blades - 1)) * spread;
    const len = 30 + rng() * 20;
    const w = 3 + rng() * 2.2;
    const tx = cx + Math.cos(a) * len;
    const ty = cy + Math.sin(a) * len;
    const cl = Math.cos(a + Math.PI / 2);
    const sl = Math.sin(a + Math.PI / 2);
    const mx = cx + Math.cos(a) * len * 0.6;
    const my = cy + Math.sin(a) * len * 0.6;
    p += `<path d="M${cx + cl * w} ${cy + sl * w} Q${mx + cl * w * 1.6} ${my + sl * w * 1.6} ${tx} ${ty} Q${mx - cl * w * 1.6} ${my - sl * w * 1.6} ${cx - cl * w} ${cy - sl * w} Z" fill="${color}"/>`;
  }
  return p;
}

function columnar(rng: () => number, color: string): string {
  // saguaro — central column with 0..2 arms
  const w = 11 + rng() * 3;
  const h = 46 + rng() * 14;
  const baseY = 92;
  const topY = baseY - h;
  let p = `<rect x="${50 - w / 2}" y="${topY}" width="${w}" height="${h}" rx="${w / 2}" fill="${color}"/>`;
  const arms = Math.floor(rng() * 3);
  for (let i = 0; i < arms; i++) {
    const side = i === 0 ? -1 : rng() > 0.5 ? -1 : 1;
    const armY = topY + 10 + rng() * 16;
    const reach = 10 + rng() * 7;
    const aw = w * 0.62;
    const elbowY = armY - (8 + rng() * 8);
    p += `<path d="M${50 + side * (w / 2)} ${armY} q${side * reach} 0 ${side * reach} ${-(armY - elbowY) * 0.5} l0 ${-(elbowY - topY - 4)}" fill="none" stroke="${color}" stroke-width="${aw}" stroke-linecap="round" stroke-linejoin="round"/>`;
  }
  return p;
}

function barrel(rng: () => number, color: string): string {
  // barrel cactus — squat ribbed dome with a radial crown
  const rx = 26 + rng() * 5;
  const ry = 30 + rng() * 6;
  const cy = 92 - ry;
  let p = `<ellipse cx="50" cy="${cy}" rx="${rx}" ry="${ry}" fill="${color}"/>`;
  const ribs = 5 + Math.floor(rng() * 3);
  for (let i = 0; i < ribs; i++) {
    const t = i / (ribs - 1);
    const x = 50 + (t - 0.5) * rx * 1.5;
    p += `<path d="M${x} ${cy - ry * 0.7} Q${50 + (t - 0.5) * rx * 0.4} ${cy} ${x} ${cy + ry * 0.85}" fill="none" stroke="rgba(255,255,255,.35)" stroke-width="1.6" stroke-linecap="round"/>`;
  }
  // crown flower
  p += `<circle cx="50" cy="${cy - ry * 0.78}" r="4.5" fill="${color}" opacity="1"/>`;
  return p;
}

function paddle(rng: () => number, color: string): string {
  // prickly pear — stacked pads with dot areoles
  const pads = 2 + Math.floor(rng() * 2);
  let p = '';
  let x = 50;
  let y = 78;
  let rx = 16 + rng() * 3;
  let ry = 20 + rng() * 3;
  for (let i = 0; i < pads; i++) {
    p += `<ellipse cx="${x}" cy="${y}" rx="${rx}" ry="${ry}" fill="${color}"/>`;
    for (let d = 0; d < 5; d++) {
      p += `<circle cx="${x + (rng() - 0.5) * rx}" cy="${y + (rng() - 0.5) * ry}" r="1.1" fill="rgba(255,255,255,.5)"/>`;
    }
    const dir = rng() > 0.5 ? 1 : -1;
    x += dir * rx * 0.8;
    y -= ry * 0.95;
    rx *= 0.8;
    ry *= 0.8;
  }
  return p;
}

const ARCHETYPES = [rosette, columnar, barrel, paddle];

export interface CollectionMarkProps {
  /** Stable seed — the collection id (falls back to title). */
  seed: string;
  className?: string;
  /** Fill opacity of the whole mark (soft by default so it reads as a cover motif). */
  opacity?: number;
}

export default function CollectionMark({ seed, className, opacity = 0.9 }: CollectionMarkProps) {
  const h = hashString(seed || 'agavi');
  const rng = mulberry32(h);
  // First draw decides species + colour so they're stable and independent of blade jitter.
  const color = PALETTE[h % PALETTE.length]!;
  const build = ARCHETYPES[Math.floor(rng() * ARCHETYPES.length)]!;
  const markup = build(rng, color);
  return (
    <svg
      viewBox="0 0 100 100"
      className={className}
      role="img"
      aria-hidden="true"
      style={{ opacity }}
      dangerouslySetInnerHTML={{ __html: markup }}
    />
  );
}

/** Exposed for callers that want the seeded accent colour (e.g. a matching hover border). */
export function markColor(seed: string): string {
  return PALETTE[hashString(seed || 'agavi') % PALETTE.length]!;
}
